import Bugsnag from '@bugsnag/browser';
import { PaymentMethodCode, PaymentOption, Sale } from '@src/core/buyerApiClient';
import { triggerExternalAuthError, triggerExternalAuthStatus } from '@src/features/payments/store/payment';
import {
  ExternalAuthenticationProvider,
  ExternalAuthenticationProviderParams,
  ExternalAuthResult,
} from '@src/features/payments/external-auth/types';
import { createHiddenInput } from '@src/features/payments/external-auth/utils';
import { observeCardinalChallenge } from '@src/features/payments/external-auth/providers/braspag/cardinalChallenge';

declare let __APP_ENV__: string;

export const BRASPAG_3DS_STATUS = {
  success: 'success',
  failure: 'failure',
  unenrolled: 'unenrolled',
  disabled: 'disabled',
  error: 'error',
  unsuportedBrand: 'unsuportedBrand',
};

export class BraspagProvider implements ExternalAuthenticationProvider {
  async handleAuthentication(params: ExternalAuthenticationProviderParams): Promise<ExternalAuthResult> {
    const { transactable, data, dispatch, paymentOption } = params;
    const { sale } = transactable;

    if (!sale) {
      return {
        proceed: false,
        error: 'tds_invalid_transactable',
        externalAuthentication: undefined,
      };
    }

    const wdw = window as any;
    const { body } = document;
    const div = this.mountBraspagInputValues(paymentOption, sale, data);

    let stopChallengeObserver: VoidFunction = () => undefined;

    const handleReturn = (done: (r: ExternalAuthResult) => void, data?: Record<string, any>): void => {
      stopChallengeObserver();
      wdw.bpmpi_config = null;
      body.removeChild(div);

      const result: ExternalAuthResult = {
        proceed: false,
        error: '',
        externalAuthentication: undefined,
      };

      if (data) {
        dispatch(triggerExternalAuthStatus(data['status']));
        Bugsnag.notify({
          name: 'ExternalAuthStatus',
          message: `Sale ${sale.id} got status ${data['status']} in 3DS attempt`,
        });

        switch (data['status']) {
          case BRASPAG_3DS_STATUS.success:
            result.proceed = true;
            result.externalAuthentication = data['e'];
            break;

          case BRASPAG_3DS_STATUS.error:
            dispatch(triggerExternalAuthError());
            result.error = 'tds_generic_error';
            result.proceed = false;
            break;

          case BRASPAG_3DS_STATUS.failure:
            result.error = 'tds_transaction_failed';
            result.proceed = false;
            break;

          case BRASPAG_3DS_STATUS.disabled:
          case BRASPAG_3DS_STATUS.unsuportedBrand:
          case BRASPAG_3DS_STATUS.unenrolled:
          default:
            result.proceed = true;
            break;
        }
      }

      done(result);
    };

    return new Promise((done) => {
      wdw.bpmpi_config = () => ({
        onReady() {
          // Cardinal's songbird is loaded by now, and it is what puts the challenge on the screen.
          stopChallengeObserver = observeCardinalChallenge(dispatch);
          wdw.bpmpi_authenticate();
        },
        onSuccess(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.success });
        },
        onFailure(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.failure });
        },
        onUnenrolled(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.unenrolled });
        },
        onDisabled(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.disabled });
        },
        onError(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.error });
        },
        onUnsupportedBrand(e: Record<string, any>) {
          handleReturn(done, { e, status: BRASPAG_3DS_STATUS.unsuportedBrand });
        },
        Environment: __APP_ENV__ === 'production' ? 'PRD' : 'SDB',
        Debug: true,
      });

      const script = document.createElement('script');
      script.src = `${params.widgetUrl}BP.Mpi.3ds20.min.js`;
      div.appendChild(script);

      body.appendChild(div);
    });
  }

  private mountBraspagInputValues(po: PaymentOption, sale: Sale, data: Record<string, any>): HTMLElement {
    const opt = po.installmentOptions.find((io: any) => io.division == data['payment_card'].installments)!;
    const div = document.createElement('div');

    this.createBraspagInput(div, 'auth', 'true');
    this.createBraspagInput(div, 'accesstoken', po.externalAuthentication!.data['access_token']);
    this.createBraspagInput(div, 'ordernumber', `${sale.id}`);
    this.createBraspagInput(div, 'currency', 'BRL');

    this.createBraspagInput(div, 'totalamount', `${opt.amountCents}`);
    this.createBraspagInput(div, 'installments', data['payment_card'].installments);
    this.createBraspagInput(div, 'paymentmethod', this.getPaymentCode(po.paymentMethod.code));
    this.createBraspagInput(div, 'cardnumber', data['payment_card'].card_number);
    this.createBraspagInput(div, 'cardexpirationmonth', data['payment_card'].validity_month);
    this.createBraspagInput(div, 'cardexpirationyear', data['payment_card'].validity_year);

    this.createBraspagInput(div, 'order_productcode', 'PHY');
    this.createBraspagInput(div, 'merchant_url', window.location.origin);

    return div;
  }

  private createBraspagInput(element: HTMLElement, name: string, value: string): void {
    createHiddenInput(element, `bpmpi_${name}`, value, `bpmpi_${name}`);
  }

  private getPaymentCode(paymentMethodCode?: string): string {
    const paymentCodes: { [key: string]: string } = {
      [PaymentMethodCode.CreditCard]: 'Credit',
      [PaymentMethodCode.DebitCard]: 'Debit',
    };
    return paymentCodes[paymentMethodCode || ''] || 'Credit';
  }
}
